import { EventEmitter, Injectable } from '@angular/core';
import { filter, take } from 'rxjs';

import { BasePusherService } from 'src/app/pusher/base-pusher.service';
import { LoggerService } from 'src/app/logger/logger.service';
import { ProfileQuery } from 'src/app/profile/state/profile.query';
import { NotificationInboxDto, NotificationTypeEnum } from '../models/notification.interface';

@Injectable()
export class NotificationsPusherService extends BasePusherService {
	protected readonly loggerName = 'NotificationsPusherService';

	notificationReceived = new EventEmitter<NotificationInboxDto>();

	private channel: any;

	/** Prevents double-binding if `init()` runs again after profile hydrates. */
	private channelBound = false;

	constructor(
		logger: LoggerService,
		profileQuery: ProfileQuery,
	) {
		super(logger, profileQuery);
	}

	init(): void {
		if (this.channelBound) {
			return;
		}
		const companyId = this.profileQuery.getCompanyId();
		const userId = this.profileQuery.getUserId();
		if (!companyId || !userId) {
			this.profileQuery
				.select()
				.pipe(
					filter((p) => !!p?.companyId && !!p?.userId),
					take(1),
				)
				.subscribe(() => this.init());
			return;
		}
		const agentId = userId.replace(/[^a-zA-Z0-9]+/g, '-');
		const channelName = `private-notifications-${companyId}-${agentId}`;
		this.channel = this.subscribeChannel(channelName);
		this.initChannelBindings(channelName);
		this.channelBound = true;
	}

	private initChannelBindings(channelName: string): void {

		this.channel.bind('pusher:subscription_succeeded', () =>
			this.logger.info(this.loggerName, `Subscription succeeded for channel ${channelName}`)
		);
		this.channel.bind('pusher:subscription_error', (error: unknown) => {
			// A missing/zero status means a transient network blip (Pusher auto-reconnects), so
			// log at `warn` to avoid paging on-call. A real auth rejection (401/403) stays `error`.
			const status = this.extractSubscriptionErrorStatus(error);
			if (status) {
				this.logger.error(this.loggerName, `Subscription error for channel ${channelName}`, error);
			} else {
				this.logger.warn(this.loggerName, `Subscription error for channel ${channelName}`, error);
			}
		});

		this.channel.bind_global((eventName: string, data: unknown) => {
			this.logger.info(this.loggerName, `Event received on channel ${channelName}: ${eventName}`);
			if (eventName.startsWith('pusher:')) {
				return;
			}
			const notification = this.parseNotificationPayload(data);
			if (!notification) {
				return;
			}
			this.notificationReceived.emit(notification);
		});
	}

	/**
	 * Pusher's `subscription_error` payload carries the auth endpoint's HTTP status when the
	 * request completed (e.g. 401/403). A transient network failure has no status (or status 0).
	 */
	private extractSubscriptionErrorStatus(error: unknown): number | undefined {
		if (typeof error === 'number') {
			return error;
		}
		if (error && typeof error === 'object' && 'status' in error) {
			const status = (error as { status: unknown }).status;
			return typeof status === 'number' ? status : undefined;
		}
		return undefined;
	}

	/**
	 * Pusher may deliver `data` as an object or as a JSON string (or leave it unparsed on parse failure).
	 */
	private parseNotificationPayload(data: unknown): NotificationInboxDto | null {
		let parsed: unknown = data;
		if (typeof data === 'string') {
			try {
				parsed = JSON.parse(data);
			} catch {
				this.logger.warn(this.loggerName, 'Could not JSON.parse Pusher event data');
				return null;
			}
		}
		if (!parsed || typeof parsed !== 'object') {
			this.logger.warn(this.loggerName, 'Pusher notification payload empty or invalid', parsed);
			return null;
		}
		const normalized = this.normalizePusherPayload(parsed as Record<string, unknown>);
		if (!normalized) {
			this.logger.warn(this.loggerName, 'Pusher notification payload missing id', parsed);
			return null;
		}
		return normalized;
	}

	/**
	 * Real-time events may send PascalCase (.NET default) while REST responses are camelCase.
	 */
	private normalizePusherPayload(raw: Record<string, unknown>): NotificationInboxDto | null {
		const id = raw['id'] ?? raw['Id'];
		if (typeof id !== 'string' || !id) {
			return null;
		}

		const notificationTypeRaw = raw['notificationType'] ?? raw['NotificationType'];
		const notificationType =
			typeof notificationTypeRaw === 'string'
				? (notificationTypeRaw as NotificationTypeEnum)
				: null;

		const metaRaw = raw['metadata'] ?? raw['Metadata'];
		let metadata: NotificationInboxDto['metadata'];
		if (metaRaw && typeof metaRaw === 'object' && !Array.isArray(metaRaw)) {
			const m = metaRaw as Record<string, unknown>;
			const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
			// templateId arrives as a number (AiTemplateReady deep link); accept a numeric string too.
			const num = (v: unknown): number | undefined =>
				typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : undefined;
			metadata = {
				assessmentId: str(m['assessmentId']) ?? str(m['AssessmentId']),
				sectionId: str(m['sectionId']) ?? str(m['SectionId']),
				assessmentQuestionId: str(m['assessmentQuestionId']) ?? str(m['AssessmentQuestionId']),
				notesThreadId: str(m['notesThreadId']) ?? str(m['NotesThreadId']),
				systemId: str(m['systemId']) ?? str(m['SystemId']),
				templateId: num(m['templateId']) ?? num(m['TemplateId']),
			};
		}

		const entityIdRaw = raw['entityId'] ?? raw['EntityId'];
		const entityId = typeof entityIdRaw === 'string' ? entityIdRaw : undefined;

		return {
			id,
			title: typeof raw['title'] === 'string' ? raw['title'] : String(raw['Title'] ?? ''),
			body: typeof raw['body'] === 'string' ? raw['body'] : String(raw['Body'] ?? ''),
			notificationType,
			isRead: Boolean(raw['isRead'] ?? raw['IsRead'] ?? false),
			createdAt:
				typeof raw['createdAt'] === 'string'
					? raw['createdAt']
					: String(raw['CreatedAt'] ?? ''),
			updatedAt: (() => {
				const u = raw['updatedAt'] ?? raw['UpdatedAt'];
				if (u === null || u === undefined) {
					return null;
				}
				return typeof u === 'string' ? u : null;
			})(),
			agentId:
				typeof raw['agentId'] === 'string' ? raw['agentId'] : String(raw['AgentId'] ?? ''),
			companyId:
				typeof raw['companyId'] === 'string' ? raw['companyId'] : String(raw['CompanyId'] ?? ''),
			entityId,
			metadata,
		};
	}

	override disconnect(): void {
		super.disconnect();
		this.channelBound = false;
	}
}
